import { useEffect, useState, type FormEvent } from "react";
import type { Person, PlanDetail, SongDetail } from "../../../shared/types";
import { durationLabel, formatTime } from "../../../shared/time";
import { Dialog, Empty, ErrorNote } from "../../ui";
import { addItem, deleteItem, listPeople, loadSong, reorderItems, updateItem, type ItemPatch } from "./client";
import { ItemRow } from "./item-row";
import { SongPicker } from "./song-picker";
import { activeFirst, countSongs, ITEM_DEFAULT_SECONDS, KIND_LABEL, messageOf, moveId, parseDuration, reorderIds, splitDuration, type BlockKind } from "./model";

export function OrderBoard({ plan, canEdit, onPlan }: {
  plan: PlanDetail;
  canEdit: boolean;
  onPlan: (plan: PlanDetail) => void;
}) {
  const [people, setPeople] = useState<Person[]>([]);
  const [songs, setSongs] = useState<Record<string, SongDetail | null>>({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [songOpen, setSongOpen] = useState(false);
  const [block, setBlock] = useState<BlockKind | null>(null);
  const songKey = plan.items.flatMap((item) => item.songId ? [item.songId] : []).toSorted().join("|");

  useEffect(() => {
    let cancel = false;
    void listPeople().then((rows) => { if (!cancel) setPeople(activeFirst(rows)); }).catch(() => { if (!cancel) setPeople([]); });
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    let cancel = false;
    const ids = songKey ? songKey.split("|") : [];
    void Promise.all(ids.map(async (songId) => {
      try {
        return [songId, await loadSong(songId)] as const;
      } catch {
        return [songId, null] as const;
      }
    })).then((rows) => { if (!cancel) setSongs(Object.fromEntries(rows)); });
    return () => { cancel = true; };
  }, [songKey]);

  async function commit(ids: string[] | null) {
    setDragId(null);
    setOverId(null);
    if (!ids) return;
    setBusy(true);
    setError("");
    try {
      onPlan(await reorderItems(plan.id, ids));
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy(false);
    }
  }

  async function patch(itemId: string, body: ItemPatch) {
    setBusy(true);
    setError("");
    try {
      onPlan(await updateItem(itemId, body));
    } catch (caught) {
      setBusy(false);
      throw caught;
    }
    setBusy(false);
  }

  async function remove(itemId: string) {
    setBusy(true);
    setError("");
    try {
      onPlan(await deleteItem(itemId));
    } catch (caught) {
      setBusy(false);
      throw caught;
    }
    setBusy(false);
  }

  const ids = plan.items.map((item) => item.id);

  return (
    <div className="stack">
      <div className="split">
        <div>
          <h2>Order</h2>
          <p className="muted">{countSongs(plan)} songs · {plan.items.length} rows · {durationLabel(plan.totalSeconds)} · ends {formatTime(plan.endsAt)}</p>
        </div>
        {canEdit && (
          <div className="cluster">
            <button className="btn primary" type="button" onClick={() => setSongOpen(true)}>Add song</button>
            {(["header", "reading", "media", "break"] as const).map((kind) => (
              <button key={kind} className="btn small" type="button" onClick={() => setBlock(kind)}>{KIND_LABEL[kind]}</button>
            ))}
          </div>
        )}
      </div>
      <p className="muted">Times follow the plan start and each row’s length. Move a row and the clock updates.</p>
      {plan.totalSeconds < 60 && <p className="faint">The scheduled end stays at least one minute after the start, even when the order is shorter.</p>}
      {error && <ErrorNote>{error}</ErrorNote>}
      {plan.items.length === 0 && (
        <Empty title="The order is empty" body="Add a song from the library, or a header, reading, media cue, or break." action={canEdit ? <button className="btn primary" type="button" onClick={() => setSongOpen(true)}>Add song</button> : undefined} />
      )}
      <ol className="svc-order">
        {plan.items.map((item, index) => (
          <li key={item.id} id={`plan-item-${item.id}`}>
            <ItemRow
              item={item}
              index={index}
              count={plan.items.length}
              planId={plan.id}
              song={item.songId ? songs[item.songId] : undefined}
              people={people}
              canEdit={canEdit}
              busy={busy}
              dragging={dragId === item.id}
              over={overId === item.id && dragId !== item.id}
              onMove={(itemIndex, delta) => void commit(moveId(ids, itemIndex, delta))}
              onDragStart={setDragId}
              onDragOver={setOverId}
              onDrop={(draggedId, targetId) => void commit(reorderIds(ids, draggedId, targetId))}
              onDragEnd={() => { setDragId(null); setOverId(null); }}
              onPatch={patch}
              onDelete={remove}
            />
          </li>
        ))}
      </ol>
      {songOpen && <SongPicker planId={plan.id} people={people} onClose={() => setSongOpen(false)} onPlan={onPlan} />}
      {block && (
        <BlockDialog
          kind={block}
          planId={plan.id}
          people={people}
          onClose={() => setBlock(null)}
          onPlan={onPlan}
        />
      )}
    </div>
  );
}

function BlockDialog({ kind, planId, people, onClose, onPlan }: {
  kind: BlockKind;
  planId: string;
  people: Person[];
  onClose: () => void;
  onPlan: (plan: PlanDetail) => void;
}) {
  const initial = splitDuration(ITEM_DEFAULT_SECONDS[kind]);
  const [title, setTitle] = useState(KIND_LABEL[kind]);
  const [minutes, setMinutes] = useState(String(initial.minutes));
  const [seconds, setSeconds] = useState(String(initial.seconds));
  const [description, setDescription] = useState("");
  const [leaderId, setLeaderId] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const durationSeconds = parseDuration(minutes, seconds);
    if (!title.trim()) { setError("Give this row a title."); return; }
    if (durationSeconds === null) { setError("Use a duration up to 8 hours."); return; }
    setBusy(true);
    setError("");
    try {
      onPlan(await addItem(planId, {
        kind,
        title: title.trim(),
        durationSeconds,
        description,
        leaderPersonId: leaderId || null,
      }));
      onClose();
    } catch (caught) {
      setError(messageOf(caught));
      setBusy(false);
    }
  }

  return (
    <Dialog title={`Add ${KIND_LABEL[kind].toLowerCase()}`} onClose={onClose}>
      <form className="form-grid" onSubmit={(event) => void submit(event)}>
        {error && <ErrorNote>{error}</ErrorNote>}
        <div className="field"><label htmlFor="block-title">Title</label><input id="block-title" value={title} maxLength={200} onChange={(event) => setTitle(event.target.value)} autoFocus /></div>
        <div className="svc-editor-grid">
          <div className="field"><label htmlFor="block-minutes">Minutes</label><input id="block-minutes" type="number" min={0} max={480} value={minutes} onChange={(event) => setMinutes(event.target.value)} /></div>
          <div className="field"><label htmlFor="block-seconds">Seconds</label><input id="block-seconds" type="number" min={0} max={59} value={seconds} onChange={(event) => setSeconds(event.target.value)} /></div>
        </div>
        <div className="field"><label htmlFor="block-note">Note</label><textarea id="block-note" maxLength={4000} value={description} onChange={(event) => setDescription(event.target.value)} /></div>
        <div className="field">
          <label htmlFor="block-leader">Item leader</label>
          <select id="block-leader" value={leaderId} onChange={(event) => setLeaderId(event.target.value)}>
            <option value="">No leader</option>
            {people.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
          </select>
        </div>
        <button className="btn primary" type="submit" disabled={busy}>{busy ? "Adding…" : "Add to the order"}</button>
      </form>
    </Dialog>
  );
}

